import type { ItemResponse } from "@memorang/types";

/**
 * AI-assessor category for an AI-submittable item. Each maps to a langgraph
 * subtask the assessor understands:
 * - `speaking`   → audio is uploaded and scored as speech
 * - `writing`    → free-text response
 * - `completion` → structured response (forms, fill-in-blanks, selection, order)
 */
export type AssessmentCategory = "speaking" | "writing" | "completion";

// Maps a response discriminant to its assessor category. Covers BOTH the legacy
// response types (`recording`/`writing`/`form`) and the PTE response types
// (`RECORDED_AUDIO`/`EXTENDED_TEXT`/…). Keep in sync with `isAiSubmittable`
// (constants/index.ts): every response type that is AI-submittable must have a
// category here, or its audio/text/structured payload never reaches the assessor.
const CATEGORY_BY_RESPONSE_TYPE: Partial<
	Record<ItemResponse["type"], AssessmentCategory>
> = {
	recording: "speaking",
	RECORDED_AUDIO: "speaking",
	writing: "writing",
	EXTENDED_TEXT: "writing",
	SHORT_TEXT_ENTRY: "writing",
	form: "completion",
	INLINE_OPTION_SELECTION: "completion",
	TOKEN_MAPPING: "completion",
	INLINE_TEXT_SELECTION: "completion",
	ORDER_RECONSTRUCTION: "completion",
};

const ASSESSOR_SUBTASK_BY_CATEGORY: Record<AssessmentCategory, string> = {
	speaking: "langgraph-speaking",
	writing: "langgraph-writing",
	completion: "langgraph-completion",
};

export const getAssessmentCategory = (
	responseType?: ItemResponse["type"],
): AssessmentCategory | undefined =>
	responseType ? CATEGORY_BY_RESPONSE_TYPE[responseType] : undefined;

/**
 * Assessor `subtaskType` for a response type, or `""` when the type is not
 * AI-submittable (caller treats empty as "no AI assessment").
 */
export const getAssessorSubtaskType = (
	responseType?: ItemResponse["type"],
): string => {
	const category = getAssessmentCategory(responseType);
	return category ? ASSESSOR_SUBTASK_BY_CATEGORY[category] : "";
};
